import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export type InviteStatus =
  | "queued"
  | "pdf_generating"
  | "ready"
  | "sending_email"
  | "sending_post"
  | "sent"
  | "failed";

export interface MeetingInvite {
  id: number;
  title: string;
  date: string;
  location: string;
  description: string;

  userId: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  invoiceDeliveryMethod?: "EMAIL" | "POST";

  // Address
  street?: string;
  postalCode?: string;
  city?: string;

  // live fields coming from SSE/DB
  status?: InviteStatus;
  pct?: number;
  lastError?: string | null;
  sentAt?: string | null;

  file?: {
    id?: string; // allow id from SSE
    filename?: string;
    originalname?: string;
    path?: string;
  } | null;
}

// export interface MeetingInvite {
//   id: number;
//   title: string;
//   date: string;
//   location: string;
//   description: string;
//   sentAt?: string;
//   file?: {
//     filename: string;
//     originalname: string;
//   };
// }

interface MeetingInviteState {
  meetingInvites: MeetingInvite[];
  meetingInvite: MeetingInvite | null;
  open: boolean;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: MeetingInviteState = {
  meetingInvites: [],
  meetingInvite: null,
  open: false,
  status: "idle",
  error: null,
};

// --- Async Thunks ---
export const fetchMeetingInvites = createAsyncThunk(
  "meetingInvite/fetchMeetingInvites",
  async (associationId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/meeting-invites?associationId=${associationId}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchMeetingInvitesByMeetingId = createAsyncThunk(
  "meetingInvite/fetchMeetingInvitesByMeetingId",
  async (meetingId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/meeting-invites?meetingId=${meetingId}`
      );
      const invites: MeetingInvite[] = response.data.map((invite: any) => ({
        ...invite,
        firstName: invite.user?.firstName,
        lastName: invite.user?.lastName,
        email: invite.user?.email,
        invoiceDeliveryMethod: invite.user?.invoiceDeliveryMethod,
        street: invite.user?.address?.street,
        postalCode: invite.user?.address?.postalCode,
        city: invite.user?.address?.city,
      }));
      return invites;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createMeetingInvite = createAsyncThunk(
  "meetingInvite/createMeetingInvite",
  async (
    {
      data,
      associationId,
    }: { data: Partial<MeetingInvite>; associationId: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(`/meeting-invites`, {
        ...data,
        associationId,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateMeetingInvite = createAsyncThunk(
  "meetingInvite/updateMeetingInvite",
  async (
    { id, updates }: { id: number; updates: Partial<MeetingInvite> },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.put(
        `/meeting-invites/${id}`,
        updates
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteMeetingInvite = createAsyncThunk(
  "meetingInvite/deleteMeetingInvite",
  async ({ id }: { id: number }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/meeting-invites/${id}`);
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteMeetingInvitesBatch = createAsyncThunk(
  "meetingInvite/deleteMeetingInvitesBatch",
  async ({ ids }: { ids: number[] }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/meeting-invites/batch`, { data: { ids } });
      return ids;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateMeetingInvitesBatchSent = createAsyncThunk(
  "meetingInvite/updateMeetingInvitesBatchSent",
  async (
    { ids, associationId }: { ids: number[]; associationId?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(`/meeting-invites/send-batch`, {
        ids,
        associationId,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const generateMeetingInvitesFromUnits = createAsyncThunk(
  "meetingInvite/generateMeetingInvitesFromUnits",
  async (associationId: number, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/meeting-invites/generate-from-units?associationId=${associationId}`
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// --- Slice ---
const meetingInviteSlice = createSlice({
  name: "meetingInvite",
  initialState,
  reducers: {
    setMeetingInvite(state, action: PayloadAction<MeetingInvite>) {
      state.meetingInvite = action.payload;
    },
    openMeetingInviteForm(state, action: PayloadAction<boolean>) {
      state.open = action.payload;
      if (!action.payload) {
        state.meetingInvite = null;
      }
    },
    clearMeetingInvite(state) {
      state.meetingInvite = null;
    },
    clearMeetingInvites(state) {
      state.meetingInvites = [];
    },
    patchInvite(
      state,
      action: PayloadAction<{
        inviteId: number;
        updates: Partial<MeetingInvite>;
      }>
    ) {
      const { inviteId, updates } = action.payload;
      const idx = state.meetingInvites.findIndex((i) => i.id === inviteId);
      if (idx !== -1) {
        state.meetingInvites[idx] = {
          ...state.meetingInvites[idx],
          ...updates,
        };
      }
      // If you want to create a placeholder when not found, you can push here,
      // but since you load snapshot first, it shouldn't be necessary.
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMeetingInvites.pending, (state) => {
        state.status = "loading";
        //state.meetingInvites = [];
      })
      .addCase(fetchMeetingInvites.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.meetingInvites = action.payload;
      })
      .addCase(fetchMeetingInvites.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(fetchMeetingInvitesByMeetingId.pending, (state) => {
        state.status = "loading";
        //state.meetingInvites = [];
      })
      .addCase(fetchMeetingInvitesByMeetingId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.meetingInvites = action.payload;
      })
      .addCase(fetchMeetingInvitesByMeetingId.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      })
      .addCase(createMeetingInvite.fulfilled, (state, action) => {
        state.meetingInvites.push(action.payload);
        state.open = false;
        state.meetingInvite = null;
      })
      .addCase(updateMeetingInvite.fulfilled, (state, action) => {
        const index = state.meetingInvites.findIndex(
          (invite) => invite.id === action.payload.id
        );
        if (index !== -1) {
          state.meetingInvites[index] = action.payload;
        }
        state.open = false;
        state.meetingInvite = null;
      })
      .addCase(deleteMeetingInvite.fulfilled, (state, action) => {
        state.meetingInvites = state.meetingInvites.filter(
          (invite) => invite.id !== action.payload
        );
      })
      .addCase(deleteMeetingInvitesBatch.fulfilled, (state, action) => {
        state.meetingInvites = state.meetingInvites.filter(
          (invite) => !action.payload.includes(invite.id)
        );
      })
      .addCase(updateMeetingInvitesBatchSent.fulfilled, (state, action) => {
        const updatedInvites = action.payload;
        updatedInvites.forEach((updatedInvite: MeetingInvite) => {
          const index = state.meetingInvites.findIndex(
            (invite) => invite.id === updatedInvite.id
          );
          if (index !== -1) {
            state.meetingInvites[index] = updatedInvite;
          }
        });
      })
      .addCase(generateMeetingInvitesFromUnits.pending, (state) => {
        state.status = "loading";
      })
      .addCase(generateMeetingInvitesFromUnits.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(generateMeetingInvitesFromUnits.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

export const {
  setMeetingInvite,
  openMeetingInviteForm,
  clearMeetingInvite,
  clearMeetingInvites,
  patchInvite,
} = meetingInviteSlice.actions;

export default meetingInviteSlice.reducer;
